import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useRowClick } from '@/lib/navigation';
import { userHomesState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import { formatRelative } from '@/util/dates';
import { type UserId, homeLifecycle } from '@rock/types';
import type { FC } from 'react';

export const UserHomesTab: FC<{ userId: UserId }> = ({ userId }) => {
    const [homes, homesLoadable] = useLoadableAtomValue(userHomesState(userId), []);
    const onRowClick = useRowClick();

    return (
        <Card>
            <CardHeader>
                <CardTitle>Homes</CardTitle>
                <CardDescription>Every home this user is a member of, including soft-deleted ones.</CardDescription>
            </CardHeader>
            <CardContent>
                {homes.length === 0 ? (
                    <div className="section-description">
                        {homesLoadable.state === 'loading'
                            ? 'Loading homes…'
                            : homesLoadable.state === 'hasError'
                              ? 'Couldn’t load homes.'
                              : 'No homes yet.'}
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Home</TableHead>
                                <TableHead>ZIP</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="text-right">Created</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {homes.map((home) => (
                                <TableRow
                                    key={home.id}
                                    className="cursor-pointer transition-colors hover:bg-muted/50"
                                    onClick={onRowClick(`/directory/homes/${encodeURIComponent(home.id)}`)}
                                >
                                    <TableCell className="font-medium">
                                        <div className="flex flex-col">
                                            <span>{home.name}</span>
                                            <span className="text-xs text-muted-foreground">{home.id}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>{home.zip}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1.5">
                                            {home.active ? (
                                                <Badge variant="default">Active</Badge>
                                            ) : (
                                                <Badge variant="destructive">Deleted</Badge>
                                            )}
                                            {homeLifecycle(home) !== 'active' && (
                                                <Badge variant="secondary">
                                                    {homeLifecycle(home) === 'setting-up' ? 'Setting up' : 'Reviewing'}
                                                </Badge>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right text-muted-foreground">
                                        {formatRelative(home.createdAt)}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
};
